import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import BookDetail from "./BookDetail";
import { bookAction } from "../actionCreators";
import ModalContainer from "./ModalContainer";
import BatchActionModal from "./BatchActionModal";
import {
  bookUpdateAction,
  bookUpdateInit,
  bookUpdateBatchAction
} from "../actionCreators";
import { logout } from "../../../common/actionCreators/";
import baseApi from "../../../common/api/baseApi";

const UPDATE_BOOK_SUCCESS_MSG = "변경이 완료되었습니다.";

const BookDetailContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [ordNum, setOrdNum] = useState();
  const [open, setOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchBooks, setBatchBooks] = useState([]);

  const { book, bookError, isBookUpdated, isBookUpdating } = useSelector(
    ({ bookReducer, bookUpdateReducer }) => ({
      book: bookReducer.book,
      bookError: bookReducer.bookError,
      isBookRequesting: bookReducer,
      isBookUpdated: bookUpdateReducer.isBookUpdated,
      isBookUpdating: bookUpdateReducer.isBookUpdating
    })
  );

  const isAdmin = window.localStorage.getItem("adminYn");
  let isRequest = false;
  let isApproval = false;

  if (book) {
    isRequest = book.status === "REQUEST";
    isApproval = book.status === "APPROVAL";
  }

  const onUpdateClick = useCallback(() => {
    setOpen(true);
  }, []);

  const onCancleClick = useCallback(() => {
    const status = "CANCLE";
    dispatch(bookUpdateAction({ ordNum, status }, null));
  }, [ordNum, dispatch]);

  const onRegisterClick = useCallback(() => {
    const status = "RECEIPT";
    dispatch(bookUpdateAction({ ordNum, status }, null));
  }, [ordNum, dispatch]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onClickCancel = useCallback(
    e => {
      e.preventDefault();
      navigate("/");
    },
    [navigate]
  );

  useEffect(() => {
    // 토큰 만료(F00001)는 baseApi.interceptors.response 에서 이미 처리.
    // 그 외 에러는 강제 로그아웃 없이 로깅만 — 사용자 세션 유지.
    if (bookError) {
      // eslint-disable-next-line no-console
      console.warn("[bookDetail] api error:", bookError);
    }
  }, [bookError]);

  useEffect(() => {
    const ordNum = location.pathname.split("/")[2];
    setOrdNum(ordNum);
    setBatchBooks([]);
    dispatch(bookAction(ordNum));
  }, [dispatch, location.pathname]);

  // 책 정보를 받으면 batchNum 으로 같은 묶음 도서 fetch
  useEffect(() => {
    if (book && book.batchNum) {
      baseApi
        .get(`/book/batch/${book.batchNum}`, {
          headers: { token: localStorage.getItem("token") }
        })
        .then(res => {
          setBatchBooks((res.data && res.data.books) || []);
        })
        .catch(() => {
          setBatchBooks([]);
        });
    } else {
      setBatchBooks([]);
    }
  }, [book]);

  const onClickBatchItem = useCallback(
    targetOrdNum => {
      navigate(`/book/${targetOrdNum}`);
    },
    [navigate]
  );

  // 책별 인라인 액션 (admin 전용). 반려는 prompt 으로 사유 받음.
  const onPerBookAction = useCallback(
    (targetBook, nextStatus) => {
      if (!targetBook || !targetBook.ordNum) return;
      let rejectReason = null;
      if (nextStatus === "REJECT") {
        rejectReason = window.prompt("반려 사유를 입력해주세요.", "");
        if (rejectReason == null) return; // 취소
        rejectReason = rejectReason.trim();
        if (!rejectReason) {
          alert("반려 사유를 입력해주세요.");
          return;
        }
      } else {
        const label =
          nextStatus === "APPROVAL"
            ? "승인"
            : nextStatus === "RECEIPT"
            ? "수령등록"
            : nextStatus === "CANCLE"
            ? "신청취소"
            : nextStatus;
        if (!window.confirm(`'${targetBook.bookName}' 을 ${label} 처리할까요?`)) {
          return;
        }
      }
      dispatch(
        bookUpdateAction({
          ordNum: targetBook.ordNum,
          status: nextStatus,
          rejectReason
        })
      );
    },
    [dispatch]
  );

  // 묶음 일괄 처리 모달 (admin)
  const onOpenBatchModal = useCallback(() => setBatchOpen(true), []);
  const onCloseBatchModal = useCallback(() => setBatchOpen(false), []);
  const onSubmitBatch = useCallback(
    (status, rejectReason) => {
      if (!book || !book.batchNum) return;
      setBatchOpen(false);
      dispatch(
        bookUpdateBatchAction({
          batchNum: book.batchNum,
          status,
          rejectReason
        })
      );
    },
    [book, dispatch]
  );

  // 묶음 신청자 본인용 일괄 신청취소.
  // 묶음에 속한 REQUEST 상태 책들이 한 번에 CANCLE 처리됨.
  const onBatchCancel = useCallback(() => {
    if (!book || !book.batchNum) return;
    if (!window.confirm("이 묶음 신청을 전체 취소할까요?\n신청 상태인 책들이 모두 취소 처리됩니다.")) {
      return;
    }
    dispatch(
      bookUpdateBatchAction({
        batchNum: book.batchNum,
        status: "CANCLE",
        rejectReason: null
      })
    );
  }, [book, dispatch]);

  useEffect(() => {
    if (isBookUpdated) {
      alert(UPDATE_BOOK_SUCCESS_MSG);
      dispatch(bookUpdateInit());
      navigate("/");
    }
  }, [isBookUpdated, dispatch, navigate]);

  const isBatch = !!(book && book.batchNum && batchBooks && batchBooks.length > 1);

  return (
    <>
      <BookDetail
        book={book}
        isAdmin={isAdmin}
        onUpdateClick={onUpdateClick}
        onCancleClick={onCancleClick}
        onRegisterClick={onRegisterClick}
        isRequest={isRequest}
        isApproval={isApproval}
        onClickCancel={onClickCancel}
        batchBooks={batchBooks}
        onClickBatchItem={onClickBatchItem}
        onPerBookAction={onPerBookAction}
        onOpenBatchModal={onOpenBatchModal}
        onBatchCancel={onBatchCancel}
        isBookUpdating={isBookUpdating}
        isBatch={isBatch}
      ></BookDetail>
      {open ? <ModalContainer onClose={onClose} ordNum={ordNum} /> : null}
      {batchOpen ? (
        <BatchActionModal
          onClose={onCloseBatchModal}
          onSubmit={onSubmitBatch}
          bookCount={batchBooks ? batchBooks.length : 0}
          hasRequest={batchBooks.some(b => b.status === "REQUEST")}
          hasApproval={batchBooks.some(b => b.status === "APPROVAL")}
        />
      ) : null}
    </>
  );
};

export default BookDetailContainer;
